<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRoute, useRouter } from "vue-router";
import { useEdaConnectionStore } from "@/stores/edaConnectionStore";
import {useI18n} from "vue-i18n";
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useSnackbarStore } from "@es-bill/es-bill-common-components";
import { PrimaryButton } from '@es-bill/es-bill-common-components';

const router = useRouter();
const route = useRoute();
const {t} = useI18n();
const edaConnectionStore = useEdaConnectionStore();
const communitiesStore = useCommunitiesStore();
const snackbarStore = useSnackbarStore();

const valid = ref(false);
const formRef = ref(null);
const ecn = ref('');
const status = ref('');

const statusOptions = ['AVAILABLE', 'NOT_AVAILABLE'];
const communitiesAvailable = computed(() => communitiesStore.allCommunities.map(c => ({...c, props: {subtitle: c.ecn + ' / ' + c.ecid}})));
const isEditMode = computed(() => !!route.params.id);

const validateEcn = value => {
  const pattern = /^(?:RC|GC|CC|RD|GD|CD)\d{6}$/;
  return pattern.test(value) || t('enterValidEcnEda');
};

const validateRequired = value => !!value || t('fieldRequired');

const loadEdaConnection = (id) => {
  edaConnectionStore.getEdaConnectionById(id).then(connection => {
    ecn.value = connection.ecn;
    status.value = connection.status;
  });
};

const submitForm = async () => {
  const { valid: isValid } = await formRef.value.validate();
  if (!isValid) {
    snackbarStore.showSnackbar(t('validationErrors'), '', 'Error', 6000, []);
    return;
  }

  if (valid.value) {
    const edaConnection = {
      ecn: ecn.value,
      status: status.value,
    };

    try {
      if (isEditMode.value) {
        await edaConnectionStore.updateEdaConnection(route.params.id, edaConnection);
        snackbarStore.showSnackbar(t('applicantMessages.updateSuccess'), '', 'Success', 4000, []);
      } else {
        await edaConnectionStore.createEdaConnection(edaConnection);
        snackbarStore.showSnackbar(t('requestSuccess'), '', 'Success', 4000, []);
      }
      router.push({ name: 'edaConnections' });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || t('unexpectedError');
      const validationDetails = error?.response?.data?.details ?
        ': ' + Object.entries(error.response.data.details).map(([field, msg]) => `${field}: ${msg}`).join(', ') : '';
      snackbarStore.showSnackbar(errorMessage + validationDetails, '', 'Error', 10000, []);
    }
  }
};

onMounted(() => {
  if (isEditMode.value) {
    loadEdaConnection(route.params.id);
  }
});
</script>

<template>
  <v-container>
    <v-form ref="formRef" v-model="valid">
      <h1 class="mb-10">{{ isEditMode ? $t('editEdaConnection') : $t('createEdaConnection') }}</h1>

      <v-autocomplete
        bg-color="white"
        :label="$t('ecn')"
        v-model="ecn"
        :items="communitiesAvailable"
        variant="outlined"
        :rules="[validateEcn]"
        :filter-keys="['title','props.subtitle']"
        item-value="ecn"
        item-title="name"
        required
        clearable
      ></v-autocomplete>

      <v-select
        bg-color="white"
        :label="$t('status')"
        v-model="status"
        variant="outlined"
        :rules="[validateRequired]"
        :items="statusOptions"
        required
        clearable
      ></v-select>

      <PrimaryButton :disabled="!valid" @click="submitForm" :text="$t('submit')" />
    </v-form>
  </v-container>
</template>

<style scoped>
</style>
